import { useEffect, useState } from "react";
import { toast } from "sonner";
import SwipeCard from "../components/SwipeCard";
import api from "../lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABEL } from "../lib/constants";
import { PageHeader, Loading } from "../components/Layout";
import { BAND } from "../lib/constants";
import { InstallBanner } from "../components/PwaInstall";

function CarouselRow({ title, items, render }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="vis-rise" data-testid={`carousel-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <h3 className="text-lg font-semibold text-[#1C2530] mb-3">{title}</h3>
      <div className="flex overflow-x-auto gap-4 hide-scrollbar pb-2 -mx-1 px-1">{items.map(render)}</div>
    </div>
  );
}

function Carousels() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [nurture, setNurture] = useState([]);
  useEffect(() => {
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/nurture").then((r) => setNurture(r.data)).catch(() => {});
  }, []);
  const today = new Date().toISOString().slice(0, 10);
  const priority = leads.filter((l) => ["priority", "hot"].includes(l.band)).slice(0, 15);
  const due = nurture.filter((l) => l.nurture_date && l.nurture_date <= today).slice(0, 15);
  const Card = (l) => (
    <div key={l.id} onClick={() => nav(`/leads/${l.id}`)} data-testid={`carousel-lead-${l.id}`}
      className="min-w-[248px] bg-white border border-[#DFDAD0] rounded-md p-4 transition-transform duration-300 hover:scale-[1.04] hover:z-10 hover:border-[#E50914]/50 cursor-pointer shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[9px] uppercase px-2 py-0.5 rounded" style={{ background: BAND[l.band], color: "#fff" }}>{l.band}</span>
        <span className="font-mono text-xs text-[#1C2530]/60 tabular-nums">{l.total_score}</span>
      </div>
      <div className="text-sm font-medium text-[#1C2530] truncate">{l.account?.name || l.contact?.name || "Lead"}</div>
      <div className="text-xs text-[#1C2530]/60 truncate mt-0.5">{l.contact?.title || l.region}</div>
      {l.nurture_date && <div className="font-mono text-[10px] text-[#E50914] mt-2">re-engage {l.nurture_date}</div>}
    </div>
  );
  if (priority.length === 0 && due.length === 0) return null;
  return (
    <div className="space-y-6">
      <CarouselRow title="Priority & Hot Leads" items={priority} render={Card} />
      <CarouselRow title="Due Today · Nurture" items={due} render={Card} />
    </div>
  );
}
import { AlertTriangle, CheckCircle2, X, Sparkles, Inbox } from "lucide-react";

function Metric({ label, value, sub }) {
  return (
    <div className="border border-[#DFDAD0] bg-white p-4">
      <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">{label}</div>
      <div className="font-mono text-3xl font-medium text-[#1C2530] mt-1 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-[#1C2530]/50 mt-1">{sub}</div>}
    </div>
  );
}

function Stat({ label, value, target }) {
  const pct = target ? Math.min(100, Math.round((value / target) * 100)) : 0;
  const hit = target && value >= target;
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-[#F5F3EE]/70">{label}</span>
        <span className="font-mono text-sm text-[#F5F3EE] tabular-nums">{value}<span className="text-[#F5F3EE]/40">/{target ?? "—"}</span></span>
      </div>
      <div className="h-1.5 bg-[#F5F3EE]/15 mt-1.5">
        <div className={`h-full ${hit ? "bg-[#7FBF8A]" : "bg-[#3E8F99]"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function MobileTodayStack({ call_retries, awaiting_reply, snooze, nudgeAct, nav }) {
  const undo = (label, fn) => toast(label, { action: { label: "Undo", onClick: fn } });
  if (call_retries.length === 0 && awaiting_reply.length === 0) return null;
  return (
    <div className="md:hidden mt-3 space-y-2" data-testid="today-mobile-stack">
      <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Swipe → snooze · ← done · tap opens</div>
      {call_retries.map((r) => (
        <SwipeCard key={r.id} testId={`swipe-retry-${r.id}`} rightLabel="Snooze +1d" leftLabel="Done" onTap={() => nav(`/leads/${r.id}`)}
          onRight={() => snooze(r.id, 1)} onLeft={() => { snooze(r.id, 3); undo("Retry cleared for 3 days", () => nav(`/leads/${r.id}`)); }}>
          <div className="text-sm font-medium text-[#1C2530]">{r.account_name || "Lead"}</div>
          <div className="text-xs text-[#E2711D]">Call retry · {r.retry_call_reason}</div>
        </SwipeCard>
      ))}
      {awaiting_reply.map((a) => (
        <SwipeCard key={a.id} testId={`swipe-await-${a.id}`} rightLabel="Snooze +3d" leftLabel="Close" onTap={() => nav(`/mail?followup=${a.id}`)}
          onRight={() => nudgeAct(a.id, "snooze")} onLeft={() => nudgeAct(a.id, "close")}>
          <div className="text-sm font-medium text-[#1C2530]">{a.contact_name || a.to}</div>
          <div className="text-xs text-[#b1560f]">Awaiting reply · {a.subject} · {a.business_days_waiting} business days</div>
        </SwipeCard>
      ))}
    </div>
  );
}

function TodayCard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const key = "today-card-dismissed";

  useEffect(() => {
    api.get("/dashboard/today").then((r) => {
      if (!r.data) return;
      setData(r.data);
      setDismissed(localStorage.getItem(key) === r.data.date);
    }).catch(() => {});
  }, []);

  if (!data || dismissed) return null;
  const { metrics, targets, nurture_due, trends, call_retries = [], awaiting_reply = [] } = data;
  const close = () => { localStorage.setItem(key, data.date); setDismissed(true); };
  const snooze = async (leadId, days) => {
    try {
      const r = await api.post(`/leads/${leadId}/retry-snooze?days=${days}`);
      toast.success(`Retry pushed to ${r.data.retry_call_at}`);
      setData({ ...data, call_retries: call_retries.filter((x) => x.id !== leadId) });
    } catch (e) { toast.error(e.response?.data?.detail || "Snooze failed"); }
  };
  const nudgeAct = async (tid, action) => {
    try {
      await api.post(`/mail/awaiting-reply/${tid}`, { action, days: 3 });
      toast.success(action === "snooze" ? "Snoozed 3 days" : "Marked closed");
      setData({ ...data, awaiting_reply: awaiting_reply.filter((x) => x.id !== tid) });
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };
  const imp = trends?.improvement, det = trends?.deterioration;

  return (
    <div className="relative bg-[#1C2530] p-5 text-[#F5F3EE]" data-testid="today-card">
      <button onClick={close} data-testid="today-card-dismiss" className="absolute top-4 right-4 text-[#F5F3EE]/50 hover:text-[#F5F3EE]"><X className="w-4 h-4" /></button>
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[#F5F3EE]/60 mb-4">
        <Sparkles className="w-3.5 h-3.5 text-[#3E8F99]" /> Today · your standing vs targets
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <Stat label="Calls" value={metrics.calls} target={targets.calls} />
        <Stat label="Meetings" value={metrics.meetings} target={targets.meetings} />
        <Stat label="Replies" value={metrics.replies} target={targets.replies} />
        <Stat label="Deals" value={metrics.deals} target={targets.deals} />
      </div>
      {trends && (
        <div className="mt-4 pt-3 border-t border-[#F5F3EE]/10 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="today-card-trends">
          <div className="font-mono text-[11px] text-[#5FB37A]" data-testid="trend-improvement">
            ▲ {imp.stage_advances.length} advancing · {imp.meetings_booked} meetings booked · {imp.score_increases.length} score gains
          </div>
          <div className="font-mono text-[11px] text-[#E07A6E]" data-testid="trend-deterioration">
            ▼ {det.gone_cold.length} gone cold · {det.stage_aging.length} stage-aging · {det.moved_to_lost.length} to Lost
          </div>
        </div>
      )}
      {nurture_due > 0 && (
        <div className="mt-3 font-mono text-[11px] text-[#E0A458]" data-testid="today-card-nurture">
          {nurture_due} nurture lead{nurture_due > 1 ? "s" : ""} due for re-engagement today
        </div>
      )}
      <MobileTodayStack call_retries={call_retries} awaiting_reply={awaiting_reply} snooze={snooze} nudgeAct={nudgeAct} nav={nav} />
      {awaiting_reply.length > 0 && (
        <div className="mt-3 hidden md:block" data-testid="today-card-awaiting">
          <div className="font-mono text-[11px] text-[#b1560f]">{awaiting_reply.length} email{awaiting_reply.length > 1 ? "s" : ""} awaiting reply · 3+ business days</div>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {awaiting_reply.map((a) => (
              <span key={a.id} className="inline-flex items-stretch border border-[#b1560f]/50 text-[11px]" data-testid={`awaiting-chip-${a.id}`}>
                <button onClick={() => nav(`/mail?followup=${a.id}`)} title="Follow up with an AI draft" className="px-2 py-0.5 text-[#1C2530] hover:bg-[#b1560f]/10">{a.contact_name || a.to} <span className="text-[#1C2530]/50">· {a.subject} · {a.business_days_waiting}bd</span></button>
                <button onClick={() => nudgeAct(a.id, "snooze")} title="Snooze 3 days" data-testid={`awaiting-snooze-${a.id}`} className="px-1.5 border-l border-[#b1560f]/40 font-mono text-[9px] text-[#1C2530]/60 hover:bg-[#b1560f]/10">+3d</button>
                <button onClick={() => nudgeAct(a.id, "close")} title="Mark closed" data-testid={`awaiting-close-${a.id}`} className="px-1.5 border-l border-[#b1560f]/40 font-mono text-[9px] text-[#1C2530]/60 hover:bg-[#b1560f]/10">✓</button>
              </span>
            ))}
          </div>
        </div>
      )}
      {call_retries.length > 0 && (
        <div className="mt-3 hidden md:block" data-testid="today-card-retries">
          <div className="font-mono text-[11px] text-[#E2711D]">{call_retries.length} call retr{call_retries.length > 1 ? "ies" : "y"} due · voicemail / no answer</div>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {call_retries.map((r) => (
              <span key={r.id} className="inline-flex items-stretch border border-[#E2711D]/50 text-[11px]" data-testid={`retry-chip-${r.id}`}>
                <button onClick={() => nav(`/leads/${r.id}`)} className="px-2 py-0.5 text-[#1C2530] hover:bg-[#E2711D]/10">{r.account_name || "Lead"} <span className="text-[#1C2530]/50">· {r.retry_call_reason}</span></button>
                <button onClick={() => snooze(r.id, 1)} title="Push retry +1 day" data-testid={`retry-snooze-1-${r.id}`} className="px-1.5 border-l border-[#E2711D]/40 font-mono text-[9px] text-[#1C2530]/60 hover:bg-[#E2711D]/10">+1d</button>
                <button onClick={() => snooze(r.id, 3)} title="Push retry +3 days" data-testid={`retry-snooze-3-${r.id}`} className="px-1.5 border-l border-[#E2711D]/40 font-mono text-[9px] text-[#1C2530]/60 hover:bg-[#E2711D]/10">+3d</button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DiscoveryNudge() {
  const nav = useNavigate();
  const [added, setAdded] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    api.get("/me/mailbox/scan/summary").then((r) => setAdded(r.data.added_recent || 0)).catch(() => {});
  }, []);
  const ack = () => api.post("/me/mailbox/scan/ack").catch(() => {});
  if (!added || dismissed) return null;
  return (
    <div className="relative bg-[#1C2530] text-[#F5F3EE] p-4 flex items-center gap-3" data-testid="discovery-nudge">
      <Inbox className="w-5 h-5 text-[#3E8F99] shrink-0" strokeWidth={1.5} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{added} new lead{added > 1 ? "s" : ""} added from your inbox</div>
        <div className="text-xs text-[#F5F3EE]/60">Auto-added to your pipeline as Suspect — pick them up in the pipeline.</div>
      </div>
      <button data-testid="discovery-nudge-review" onClick={() => { ack(); nav("/pipeline"); }}
        className="bg-[#E50914] text-white px-4 py-2 text-sm hover:bg-[#c40810] shrink-0">View pipeline</button>
      <button data-testid="discovery-nudge-dismiss" onClick={() => { ack(); setDismissed(true); }}
        className="text-[#F5F3EE]/50 hover:text-[#F5F3EE] shrink-0"><X className="w-4 h-4" /></button>
    </div>
  );
}

function EscalationsCard() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/mail/escalations").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows || rows.length === 0) return null;
  return (
    <div className="border border-[#B23A2E]/40 bg-[#B23A2E]/5 p-4" data-testid="escalations-card">
      <div className="font-mono text-[10px] uppercase tracking-wider text-[#B23A2E] mb-2">Escalations · hot leads unanswered 5+ business days</div>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <button key={r.id} onClick={() => nav(`/leads/${r.lead_id}`)} data-testid={`escalation-${r.id}`} className="w-full text-left flex flex-wrap items-center gap-2 border border-[#DFDAD0] bg-white px-3 py-2 text-sm hover:bg-[#EFECE5]">
            <span className="font-medium text-[#1C2530]">{r.escalation?.contact || "—"}</span><span className="text-[#1C2530]/60">({r.escalation?.account || "—"})</span>
            <span className="text-[#1C2530]/60">· rep {r.escalation?.rep}</span><span className="text-[#1C2530]/60 truncate">· {r.escalation?.subject}</span>
            <span className="ml-auto font-mono text-[10px] uppercase px-1.5 py-0.5 bg-[#B23A2E]/15 text-[#B23A2E]">{r.escalation?.days} business days</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [recon, setRecon] = useState(null);

  useEffect(() => {
    const load = () => {
      api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
      if (user.role !== "rep")
        api.get("/dashboard/reconciliation").then((r) => setRecon(r.data)).catch(() => {});
    };
    load();
    const iv = setInterval(load, 15000); // 15s — counter visibly ticks up during large imports, not just at the end
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", load);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", load); };
  }, [user.role]);

  if (!stats) return (<><PageHeader eyebrow="OVERVIEW" title="Command Center" /><div className="p-8"><Loading /></div></>);

  if (stats.total_leads === 0) return (
    <>
      <PageHeader eyebrow={`SIGNED IN AS ${(ROLE_LABEL[user.role] || user.role).toUpperCase()}`} title="Command Center" />
      <div className="p-8">
        <DiscoveryNudge />
        <div className="flex flex-col items-center justify-center text-center py-24" data-testid="dashboard-empty">
          <h2 className="text-2xl font-light text-[#1C2530] mb-2">Welcome — let's fill your Command Center</h2>
          <p className="text-base text-[#1C2530]/55 max-w-md mb-6">Your metrics, funnel and score bands light up once you have leads. Import your list to see everything come alive.</p>
          <button onClick={() => nav("/import")} data-testid="dashboard-empty-import" className="bg-[#1C2530] text-[#F5F3EE] px-5 py-2.5 text-sm rounded-full hover:bg-[#2a3644] transition-colors">Import leads</button>
        </div>
      </div>
    </>
  );

  const maxFunnel = Math.max(...stats.stages_order.map((s) => stats.funnel[s] || 0), 1);
  const totalBands = Object.values(stats.bands).reduce((a, b) => a + b, 0) || 1;

  return (
    <>
      <PageHeader eyebrow={`SIGNED IN AS ${(ROLE_LABEL[user.role] || user.role).toUpperCase()}`} title="Command Center" />
      <div className="p-8 space-y-6">
        <DiscoveryNudge />
        {user.role === "rep" && <><InstallBanner /><TodayCard /></>}
        {user.role !== "rep" && <EscalationsCard />}
        <Carousels />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Metric label="Total Leads" value={stats.total_leads} sub="in pipeline" />
          <Metric label="Hot" value={stats.bands.hot} sub="Voice Agent triggered" />
          <Metric label="Priority" value={stats.bands.priority} sub="Sales Lead notified" />
          <Metric label="Open" value={stats.statuses.open || 0} sub="active leads" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 border border-[#DFDAD0] bg-white p-5">
            <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-4">Lifecycle Funnel</div>
            <div className="space-y-1.5">
              {stats.stages_order.map((s) => (
                <div key={s} className="flex items-center gap-3">
                  <div className="w-28 text-xs text-[#1C2530]/70 shrink-0">{s}</div>
                  <div className="flex-1 h-5 bg-[#F5F3EE] border border-[#DFDAD0]">
                    <div className="h-full bg-[#3E8F99]" style={{ width: `${((stats.funnel[s] || 0) / maxFunnel) * 100}%` }} />
                  </div>
                  <div className="w-8 text-right font-mono text-xs tabular-nums text-[#1C2530]">{stats.funnel[s] || 0}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-[#DFDAD0] bg-white p-5">
            <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-4">Score Bands</div>
            <div className="flex h-6 border border-[#DFDAD0] overflow-hidden mb-4">
              {["cold", "warm", "hot", "priority"].map((b) => (
                <div key={b} title={`${b}: ${stats.bands[b]}`} style={{ background: BAND[b], width: `${(stats.bands[b] / totalBands) * 100}%` }} />
              ))}
            </div>
            {["priority", "hot", "warm", "cold"].map((b) => (
              <div key={b} className="flex items-center justify-between py-1.5 border-b border-[#DFDAD0] last:border-0">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3" style={{ background: BAND[b] }} />
                  <span className="text-xs capitalize text-[#1C2530]/80">{b}</span>
                </div>
                <span className="font-mono text-sm tabular-nums">{stats.bands[b]}</span>
              </div>
            ))}
          </div>
        </div>

        {recon && (
          <div className={`border p-5 flex flex-wrap items-center gap-3 ${recon.balanced ? "border-[#3E8F99] bg-[#3E8F99]/5" : "border-[#B23A2E] bg-[#B23A2E]/5"}`}
            data-testid="reconciliation-panel">
            {recon.balanced ? <CheckCircle2 className="w-5 h-5 text-[#3E8F99]" strokeWidth={1.5} /> : <AlertTriangle className="w-5 h-5 text-[#B23A2E]" strokeWidth={1.5} />}
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Closed-Loop Reconciliation</div>
              <div className="text-sm text-[#1C2530]">
                {recon.total_created} created = {recon.sum_across_statuses} across statuses —{" "}
                <span className="font-medium">{recon.balanced ? "BALANCED · no leakage" : "MISMATCH · leads leaking"}</span>
              </div>
            </div>
            <div className="ml-auto flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs">
              {Object.entries(recon.by_status).map(([k, v]) => (
                <span key={k} className="text-[#1C2530]/70 whitespace-nowrap">{k}:<span className="text-[#1C2530] ml-1 tabular-nums">{v}</span></span>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
